// Scenario comparison scoring + client-ready circularity brief export.
import { systemLabel } from "./kbLogic";
import { warn } from "./log";

// All four KPIs are "higher is better"; weights sum to 1.0.
export const COMPARE_METRICS = [
  { key: "total_yield", label: "Total Yield", unit: "kg", d: 1, weight: 0.3, accent: "#A3CD40" },
  { key: "water_recovery", label: "Water Recovery", unit: "%", d: 0, weight: 0.25, accent: "#498DCC" },
  { key: "energy_offset", label: "Energy Offset", unit: "%", d: 0, weight: 0.25, accent: "#D2812E" },
  { key: "system_health", label: "Circularity", unit: "/100", d: 0, weight: 0.2, accent: "#212E20" },
];

export function fmt(n, d = 1) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toFixed(d);
}

export function cropLabel(kb, id) {
  const c = (kb?.crops || []).find((x) => x.id === id);
  return c ? `${c.crop} · ${c.cultivar}` : id;
}

export function resourceLabel(kb, id) {
  const r = (kb?.resources || []).find((x) => x.id === id);
  return r ? r.label : id;
}

// Min-max normalize each metric across the compared set, then weighted composite (0–100).
export function scoreScenarios(scenarios) {
  const ranges = {};
  COMPARE_METRICS.forEach((m) => {
    const arr = scenarios.map((s) => Number(s.kpis?.[m.key]) || 0);
    ranges[m.key] = { min: Math.min(...arr), max: Math.max(...arr) };
  });
  return scenarios.map((s) => {
    let score = 0;
    COMPARE_METRICS.forEach((m) => {
      const v = Number(s.kpis?.[m.key]) || 0;
      const { min, max } = ranges[m.key];
      const norm = max > min ? (v - min) / (max - min) : 1;
      score += norm * m.weight;
    });
    return { id: s.id, score: Math.round(score * 100) };
  });
}

// Index of the leading scenario per metric (for delta highlighting).
export function bestByMetric(scenarios) {
  const best = {};
  COMPARE_METRICS.forEach((m) => {
    let bi = 0;
    let bv = -Infinity;
    scenarios.forEach((s, i) => {
      const v = Number(s.kpis?.[m.key]) || 0;
      if (v > bv) {
        bv = v;
        bi = i;
      }
    });
    best[m.key] = bi;
  });
  return best;
}

function esc(str) {
  return String(str ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );
}

function chips(items, accent) {
  if (!items.length) return `<span class="empty">None selected</span>`;
  return items.map((t) => `<span class="chip" style="border-color:${accent}55;color:${accent}">${esc(t)}</span>`).join(" ");
}

// Builds a self-contained, print-friendly one-page brief for the winning scenario.
export function buildBriefHtml(sc, kb, score, circ = null) {
  const k = sc.kpis || {};
  const sel = sc.selections || {};
  const systems = (sel.systems || []).map((id) => systemLabel(kb, id));
  const crops = (sel.crops || []).map((id) => cropLabel(kb, id));
  const resources = (sel.resources || []).map((id) => resourceLabel(kb, id));
  const site = sc.onboarding?.location || "Site not specified";
  const date = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const co2 = fmt((Number(k.energy_offset) || 0) * 0.4, 1);

  // Circularity Score weighting footnote — transparency on how the blended score was derived.
  const w = (circ && circ.weights) || { water: 0.25, energy: 0.25, nutrient: 0.25, carbon: 0.25 };
  const locked = !!(circ && circ.locked);
  const wpct = (x) => Math.round((w[x] || 0) * 100);
  const weightFootnote = locked
    ? `Circularity Score blends four resource loops using <strong>equal weighting (25% each)</strong> — locked to an assumption-free basis.`
    : `Circularity Score blends four resource loops using these weights: <strong>Water ${wpct("water")}% · Energy ${wpct("energy")}% · Nutrient ${wpct("nutrient")}% · Carbon ${wpct("carbon")}%</strong>.`;

  const kpiCards = COMPARE_METRICS.map(
    (m) => `
      <div class="kpi">
        <div class="kpi-dot" style="background:${m.accent}"></div>
        <div class="kpi-val">${fmt(k[m.key], m.d)}<span class="kpi-unit">${m.unit}</span></div>
        <div class="kpi-lbl">${m.label}</div>
      </div>`
  ).join("");

  return `<!doctype html><html><head><meta charset="utf-8"/>
<title>Circularity Brief — ${esc(sc.name)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; color: #1D1D1F; background: #fff; padding: 48px 56px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .brand { font-size: 11px; letter-spacing: 3px; color: #86868B; font-weight: 700; text-transform: uppercase; }
  h1 { font-size: 30px; letter-spacing: -1px; margin: 6px 0 2px; }
  .sub { color: #6E6E73; font-size: 14px; }
  .badge { display: inline-block; margin-top: 14px; padding: 6px 14px; border-radius: 999px; background: #A3CD4015; color: #1e8f42; font-size: 12px; font-weight: 700; letter-spacing: 0.3px; }
  .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin: 28px 0; }
  .kpi { border: 1px solid rgba(0,0,0,0.09); border-radius: 14px; padding: 16px; }
  .kpi-dot { width: 8px; height: 8px; border-radius: 999px; margin-bottom: 10px; }
  .kpi-val { font-size: 28px; font-weight: 700; letter-spacing: -1px; }
  .kpi-unit { font-size: 12px; color: #86868B; font-weight: 500; margin-left: 4px; }
  .kpi-lbl { font-size: 10px; letter-spacing: 1px; color: #86868B; text-transform: uppercase; margin-top: 4px; }
  section { margin-top: 22px; }
  .sec-title { font-size: 11px; letter-spacing: 1.5px; color: #86868B; font-weight: 700; text-transform: uppercase; margin-bottom: 10px; }
  .chip { display: inline-block; border: 1px solid; border-radius: 999px; padding: 5px 11px; font-size: 12.5px; font-weight: 500; margin: 0 4px 6px 0; }
  .empty { color: #86868B; font-size: 12.5px; }
  .meta { display: flex; gap: 26px; margin-top: 18px; padding-top: 18px; border-top: 1px solid rgba(0,0,0,0.09); }
  .meta div span { display: block; font-size: 10px; letter-spacing: 1px; color: #86868B; text-transform: uppercase; }
  .meta div strong { font-size: 15px; }
  footer { margin-top: 34px; padding-top: 16px; border-top: 1px solid rgba(0,0,0,0.09); font-size: 11px; color: #86868B; display: flex; justify-content: space-between; }
  .assume { margin-top: 14px; font-size: 10.5px; line-height: 1.5; color: #86868B; }
  .assume strong { color: #1D1D1F; }
  @media print { body { padding: 24px 28px; } @page { margin: 12mm; } }
</style></head>
<body>
  <div class="brand">Resource Circularity Engine — Circularity Brief</div>
  <h1>${esc(sc.name)}</h1>
  <div class="sub">${esc(site)} · Generated ${esc(date)}</div>
  <div class="badge">Recommended scenario · Overall ranking ${score}/100</div>

  <div class="kpis">${kpiCards}</div>

  <section>
    <div class="sec-title">BIA Systems (${systems.length})</div>
    ${chips(systems, "#D2812E")}
  </section>
  <section>
    <div class="sec-title">Crop Program (${crops.length})</div>
    ${chips(crops, "#A3CD40")}
  </section>
  <section>
    <div class="sec-title">Circular Resource Loops (${resources.length})</div>
    ${chips(resources, "#212E20")}
  </section>

  <div class="meta">
    <div><span>Cultivated Area</span><strong>${fmt(k.total_area, 0)} m²</strong></div>
    <div><span>Est. Agriculture Carbon Savings</span><strong>${co2} t CO₂e</strong></div>
    <div><span>Active Loops</span><strong>${resources.length}</strong></div>
    <div><span>Species</span><strong>${crops.length}</strong></div>
  </div>

  <footer>
    <span>Resource Circularity Engine · www.resourcecircularity.com</span>
    <span>Confidential — prepared for client review</span>
  </footer>
  <div class="assume" data-testid="brief-weight-footnote">
    <strong>Methodology note.</strong> ${weightFootnote} Loop weights are a user-set <em>assumption</em> with no empirical basis and do not change the underlying per-loop measurements — they only determine how the loops are blended into the single score.
  </div>
</body></html>`;
}

// Opens the brief in a new tab (via a sandboxed Blob URL, no document.write) and triggers
// the browser print / save-as-PDF dialog.
export function exportBrief(sc, kb, score, circ = null) {
  const html = buildBriefHtml(sc, kb, score, circ);
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  const w = window.open(url, "_blank");
  if (!w) { URL.revokeObjectURL(url); return false; }
  w.focus();
  setTimeout(() => {
    try {
      w.print();
    } catch (err) {
      warn("brief print dialog failed", err);
    }
    URL.revokeObjectURL(url);
  }, 600);
  return true;
}
